import { __decorate } from "tslib";
import { html, nothing } from 'lit';
import { CipublicElement } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/mixins/base.js';
import { property, state, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { FormRegistrationMixin } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/form/form-registration.js';
import { ValidityMixin } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/form/validity.js';
import { IMaskController } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/imask/imask.js';
import { InternalI18n } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/i18n/internal-i18n-controller.js';
import { addStyleSheetToElements } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/utils/cssstylesheet-interface.js';
import { emit } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/utils/event.js';
import { sanitize } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/utils/sanitize.js';
import { submitForm } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/utils/formdata.js';
import '@cigna/cigna_csg_gsg_cipublic-shared/lib/utils/polyfills/request-submit.js';
import '@cigna/cigna_csg_gsg_cipublic-icon/cipublic-icon.js';
import styles from './input.scss.js';
import slotStyles from './slots.scss.js';
let counter = 0;
/**
 * @param Base The base class.
 * @returns Input component.
 *
 * @slot link - `<cipublic-link>`.
 * @slot button - Button text.
 * @slot icon - `<cipublic-icon>`
 * @slot password - type `password`.
 *
 * @fires input.blur
 * @fires input.change
 * @fires input.focus
 * @fires input.input
 * @fires input.keydown
 * @fires input.keyup
 *
 * @fires input.button-blur
 * @fires input.button-click
 * @fires input.button-focus
 * @fires input.button-keydown
 * @fires input.button-keyup
 *
 * @fires input.link-click
 * @fires input.password-button-click
 *
 * @event submit - Fires when the form is submitted.
 * @event formdata - Add form data to the submit process.
 */
export class Input extends FormRegistrationMixin(ValidityMixin(CipublicElement)) {
    constructor() {
        super(...arguments);
        this.i18n = new InternalI18n(this);
        /**
         * Sets the element to be focused automatically on page load.
         */
        this.autofocus = false;
        /**
         * Indicate if the element is disabled.
         *
         * **Note:** Will remove the element from `formdata` participation.
         */
        this.disabled = false;
        /**
         * Enable emit of connected-callback event
         */
        this.emitConnectedCallback = false;
        /**
         * Toggle element visibility.
         *
         * **Note:** Will remove the element from `formdata` participation.
         *
         * To have hidden input participate in form submission process, set
         * `type` to hidden. This will also hide the entire component.
         */
        this.hidden = false;
        /**
         * ID to associate the label with the element.
         */
        this._id = ++counter;
        /**
         * Visually hide the input label.
         *
         * **Note**: This should only be used for very specific use-cases:
         *
         * - Using a radioitem/checkitem with an input ('Other' text field)
         */
        this.labelVisuallyHidden = false;
        /**
         * Constructor name.
         */
        this._name = 'input';
        /**
         * Optional Text.
         */
        this.optional = this.i18n.t('optional');
        /**
         * Indicate if the element is required.
         */
        this.required = false;
        /**
         * The internal value.
         */
        this._value = '';
        /**
         * Input button will act as submit
         */
        this.buttonAsSubmit = false;
        /**
         * Custom width for the element.
         */
        this.width = 'width-full';
        /**
         * Auto Capitalize for input
         */
        this.transform = 'normal';
    }
    static get styles() {
        return [styles];
    }
    /**
     * Excludes the element from `formdata` participation if `true`.
     *
     * **Note:** This property is enabled by default for `disabled` / `hidden` elements.
     */
    get deregistered() {
        return this.disabled || this.hidden ? true : false;
    }
    /**
     * Value of the text entered.
     */
    get value() {
        const { _input } = this;
        /*
          Once we have the input we can directly query for the value
          Otherwise, default to the internal `_value` prop.
        */
        return _input ? _input.value : this._value;
    }
    set value(value) {
        const { _input } = this;
        const oldValue = this._value;
        this._value = value;
        this.requestUpdate('value', oldValue);
        /*
          If we programtically modify the value through assignment,
          (example: sanitizing the value), update the input element's value
        */
        if (_input) {
            _input.value = value;
        }
    }
    connectedCallback() {
        super.connectedCallback();
        if (this.emitConnectedCallback) {
            emit(this, 'connected-callback');
        }
    }
    getFirstErroredInnerElement() {
        var _a;
        return (_a = this._getFirstErroredElement()) === null || _a === void 0 ? void 0 : _a.querySelector('input');
    }
    _handleFormSubmit(event) {
        var _a;
        const { _input } = this;
        this.type === 'search' ? this.value = sanitize(this.value).trim() : this.value = sanitize(this.value);
        const error = this._showError(_input);
        this._showSuccess(_input);
        if (error) {
            // halt form submission behavior
            event.preventDefault();
            (_a = this.getFirstErroredInnerElement()) === null || _a === void 0 ? void 0 : _a.focus();
        }
        else {
            // dispatch 'FormData' event
            super._handleFormSubmit(event);
        }
    }
    handleBlur(event) {
        const { target } = event;
        const status = this._testValidity(target);
        if (status !== this.errorCustomMessage) {
            this._showError(target);
        }
        this._showSuccess(target);
        emit(this, `${this._name}.blur`);
    }
    handleChange() {
        emit(this, `${this._name}.change`);
        emit(this, 'mask-change');
    }
    handleInput() {
        emit(this, `${this._name}.input`);
        emit(this, 'mask-change');
    }
    handleSlotChange(event) {
        const slotElements = event.target.assignedElements();
        addStyleSheetToElements(slotElements, slotStyles);
    }
    updated(changedProperties) {
        super.updated(changedProperties);
        if (this.mask) {
            new IMaskController(this, this._input, this.mask);
        }
        if (changedProperties.has('disabled')) {
            if (this.disabled) {
                this.value = '';
            }
        }
        if (changedProperties.has('errorCustomMessage')) {
            if (this.errorCustomMessage) {
                this._showError(this._input);
                this._showSuccess(this._input);
            }
        }
        if (changedProperties.has('required')) {
            if (!this.required) {
                this._showSuccess(this._input);
            }
        }
    }
    handlePasswordClick(target) {
        var _a, _b;
        const { _input } = this;
        if (_input.type === 'password') {
            _input.setAttribute('type', `${this.inputmode}`);
            target.textContent = `${this.i18n.t('hide')}`;
        }
        else {
            _input.setAttribute('type', 'password');
            target.textContent = `${this.i18n.t('show')}`;
        }
        (_b = (_a = target.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('button')) === null || _b === void 0 ? void 0 : _b.focus();
        emit(this, `${this._name}.password-button-click`);
    }
    render() {
        var _a, _b;
        const { autocomplete, autofocus, disabled, hint, inputmode, label, labelVisuallyHidden, maxlength, name, optional, pattern, placeholder, required, spellcheckInput, type, width, transform, value, } = this;
        const idName = `${this._name}-${this._id}`;
        const slotClasses = {
            'input-with-element': [...(this.querySelectorAll('*') || [])]
                .map((item) => item.slot)
                .filter((slot) => {
                return ['button', 'icon', 'password'].includes(slot);
            })
                .toString(),
        };
        const labelClasses = {
            'visually-hidden': labelVisuallyHidden,
        };
        const parentForm = this.buttonAsSubmit ? (_a = this.closest('CIPUBLIC-FORM')) === null || _a === void 0 ? void 0 : _a._form : null;
        return html `<div class="element-wrapper"><div class="top-level"><label class="${classMap(labelClasses)}" for="${idName}">${label} ${!required ? optional : nothing}</label><slot name="link" @click="${() => {
            emit(this, `${this._name}.link-click`);
        }}"></slot></div><div class="element-parent"><input aria-describedby="${idName}-hint ${idName}-count ${idName}-error" aria-invalid="${false}" class="${classMap(slotClasses)}" autocomplete="${ifDefined(autocomplete)}" ?autofocus="${autofocus}" ?disabled="${((_b = this.closest('cipublic-fieldset')) === null || _b === void 0 ? void 0 : _b.disabled) ? true : disabled}" id="${idName}" inputmode="${ifDefined(inputmode)}" maxlength="${ifDefined(maxlength)}" name="${ifDefined(name)}" pattern="${ifDefined(pattern)}" placeholder="${ifDefined(placeholder)}" ?required="${disabled ? false : required}" spellcheck="${ifDefined(spellcheckInput)}" type="${ifDefined(type)}" .value="${disabled ? '' : value}" width="${width}" transform="${transform}" @blur="${this.handleBlur}" @change="${this.handleChange}" @focus="${() => emit(this, `${this._name}.focus`)}" @input="${this.handleInput}" @keydown="${(event) => {
            if (this.type === 'search' && event.key === 'Enter') {
                submitForm(this.closest('cipublic-form'));
            }
            emit(this, `${this._name}.keydown`, { detail: { event } });
        }}" @keyup="${(event) => {
            if (maxlength) {
                this._count.innerText = `${this.value.length} / ${this.maxlength}`;
            }
            emit(this, `${this._name}.keyup`, { detail: { event } });
        }}"> <span id="after" hidden></span><slot name="password" @slotchange="${this.handleSlotChange}" @click="${(event) => this.handlePasswordClick(event.target)}"></slot><slot name="button" @slotchange="${this.handleSlotChange}" @blur="${() => emit(this, `${this._name}.button-blur`)}" @click="${(event) => parentForm ? parentForm.requestSubmit() : emit(this, `${this._name}.button-click`, { detail: { event } })}" @focus="${() => emit(this, `${this._name}.button-focus`)}" @keydown="${() => emit(this, `${this._name}.button-keydown`)}" @keyup="${() => emit(this, `${this._name}.button-keyup`)}"></slot><slot name="icon" @slotchange="${this.handleSlotChange}"></slot></div><div class="bottom-level"><div>${hint ? html `<span class="hint" id="${idName}-hint">${hint}</span>` : html `${nothing}`} <span aria-live="polite" class="error-container" id="${idName}-error" role="alert"><cipublic-icon set="system" name="exclamation-circle" class="error-icon"></cipublic-icon><p></p></span></div>${maxlength ? html `<span class="count" id="${idName}-count">${maxlength ? `0 / ${maxlength}` : ''}</span>` : html `${nothing}`}</div></div>`;
    }
}
__decorate([
    property({ reflect: true })
], Input.prototype, "autocomplete", void 0);
__decorate([
    property({ reflect: true, type: Boolean })
], Input.prototype, "autofocus", void 0);
__decorate([
    query('.count')
], Input.prototype, "_count", void 0);
__decorate([
    state()
], Input.prototype, "deregistered", null);
__decorate([
    property({ reflect: true, type: Boolean })
], Input.prototype, "disabled", void 0);
__decorate([
    property({ reflect: true, type: Boolean })
], Input.prototype, "emitConnectedCallback", void 0);
__decorate([
    property({ reflect: true, type: Boolean })
], Input.prototype, "hidden", void 0);
__decorate([
    property({ reflect: true })
], Input.prototype, "hint", void 0);
__decorate([
    query('input')
], Input.prototype, "_input", void 0);
__decorate([
    property({ reflect: true })
], Input.prototype, "inputmode", void 0);
__decorate([
    property({ reflect: true })
], Input.prototype, "label", void 0);
__decorate([
    property({ reflect: true, type: Boolean })
], Input.prototype, "labelVisuallyHidden", void 0);
__decorate([
    property({ reflect: true })
], Input.prototype, "mask", void 0);
__decorate([
    property({ reflect: true, type: Number })
], Input.prototype, "maxlength", void 0);
__decorate([
    property({ reflect: true })
], Input.prototype, "name", void 0);
__decorate([
    property({ reflect: true })
], Input.prototype, "optional", void 0);
__decorate([
    property({ reflect: true })
], Input.prototype, "pattern", void 0);
__decorate([
    property({ reflect: true })
], Input.prototype, "placeholder", void 0);
__decorate([
    property({ reflect: true, type: Boolean })
], Input.prototype, "required", void 0);
__decorate([
    property({ reflect: true, attribute: 'spellcheck' })
], Input.prototype, "spellcheckInput", void 0);
__decorate([
    property({ reflect: true })
], Input.prototype, "type", void 0);
__decorate([
    state()
], Input.prototype, "_value", void 0);
__decorate([
    property({ reflect: true })
], Input.prototype, "value", null);
__decorate([
    property({ reflect: true, type: Boolean, attribute: 'button-as-submit' })
], Input.prototype, "buttonAsSubmit", void 0);
__decorate([
    property({ reflect: true })
], Input.prototype, "width", void 0);
__decorate([
    property({ reflect: true })
], Input.prototype, "transform", void 0);
//# sourceMappingURL=input.js.map